import { ColorEditor } from '@/components/UI/SpreadSheet/editors/ColorEditor';
import { DateEditorWrapper } from '@/components/UI/SpreadSheet/editors/DateEditorWrapper';
import { ExponentialEditor } from '@/components/UI/SpreadSheet/editors/ExponentialEditor';
import { MultiSelectEditor } from '@/components/UI/SpreadSheet/editors/MultiSelectEditor';

import { readMultiSelectCell, writeMultiSelectCell } from '@/components/UI/SpreadSheet/multiSelectCellTransport';

import { _isEmpty, _isNil, _set } from '@/littledash';
import type { CustomColumnSettings } from '@/components/UI/SpreadSheet/SpreadSheet.model';
import { cellText } from '@/support/HotCellValue';
import type { CellValue, HotRow, RowObject } from '@/support/hot.types';
import { requiredColumnHeader } from '@/components/UI/SpreadSheet/SpreadSheet.utils';
import type { MetadataField } from '@/model/Metadata.model';
import { DateUtils } from '@/utils/Date.utils';
import { createHotRequiredValidator, textValidatorFactory } from '@/validators';

import type { TreatmentGroupKeyedMetadata } from './CreateTreatmentGroups';
import type Handsontable from 'handsontable';
import type { ReactNode } from 'react';
import type { TreatmentGroup } from '@/model/TreatmentGroup.model';

interface ColumnDataResult {
  colHeaders: Array<string>;
  columns: Array<CustomColumnSettings>;
}

interface ColumnSettingMapperResult {
  columnHeader: string;
  columnSettings: CustomColumnSettings;
}

const inactiveColumn = (title: string) => `${title} <span class="required">(archived)</span>`;

const dateValidator =
  (required: boolean) =>
  (value: CellValue, callback: (valid: boolean) => void): void =>
    callback((_isEmpty(value) && !required) || DateUtils.isValidDate(cellText(value)));

const columnDataGetterSetterFactory =
  (metadata: MetadataField) =>
  (rowData: RowObject | Array<CellValue>, value?: CellValue): CellValue => {
    const row = rowData as HotRow<TreatmentGroupKeyedMetadata>;
    if (_isNil(row?.metadata?.[metadata.id])) {
      _set(row, ['metadata', metadata.id], {
        ...metadata,
        glossary_id: metadata.id,
        value,
      });
    }
    if (typeof value === 'undefined') {
      return row?.metadata?.[metadata.id]?.value ?? '';
    }
    _set(row, ['metadata', metadata.id], {
      ...metadata,
      glossary_id: metadata.id,
      value,
    });
    _set(row, ['updated_at'], new Date().toISOString());
  };

const selectColumnDataGetterSetterFactory =
  (metadata: MetadataField) =>
  (rowData: RowObject | Array<CellValue>, cellValue?: CellValue): CellValue => {
    const row = rowData as HotRow<TreatmentGroupKeyedMetadata>;
    const value = cellText(cellValue);
    if (_isNil(row?.metadata?.[metadata.id])) {
      _set(row, ['metadata', metadata.id], {
        ...metadata,
        glossary_id: metadata.id,
        value: value?.trim() ?? null,
      });
    }
    if (typeof value === 'undefined') {
      return row?.metadata?.[metadata.id]?.value ?? null;
    }
    _set(row, ['metadata', metadata.id], {
      ...metadata,
      glossary_id: metadata.id,
      value: value,
    });
    _set(row, ['updated_at'], new Date().toISOString());
  };

const multiSelectColumnDataGetterSetterFactory =
  (metadata: MetadataField) =>
  (rowData: RowObject | Array<CellValue>, cellValue?: CellValue): CellValue => {
    const row = rowData as HotRow<TreatmentGroupKeyedMetadata>;
    const value = cellText(cellValue);
    if (_isNil(row?.metadata?.[metadata.id])) {
      _set(row, ['metadata', metadata.id], {
        ...metadata,
        glossary_id: metadata.id,
        value: readMultiSelectCell(value ?? undefined),
      });
    }
    if (typeof value === 'undefined') {
      return writeMultiSelectCell(row?.metadata?.[metadata.id]?.value);
    }
    _set(row, ['metadata', metadata.id], {
      ...metadata,
      glossary_id: metadata.id,
      value: readMultiSelectCell(value ?? undefined),
    });
    _set(row, ['updated_at'], new Date().toISOString());
  };

const invalidSubjectNumber = (value: unknown): boolean =>
  typeof value !== 'number' || value < 0 || value > 100 || !Number.isInteger(value);

/**
 * Validator factory for the "Max Subjects" column. Ensures the value is an integer in
 * [0, 100] and not lower than the number of animals already assigned to the group
 * (read from the row's source data via the Handsontable instance).
 */
export const subjectValidator = () =>
  function (
    this: { instance: Handsontable.Core; row: number },
    value: unknown,
    callback: (valid: boolean) => void
  ): void {
    if (invalidSubjectNumber(value)) {
      callback(false);
      return;
    }

    try {
      const rowData = this.instance.getSourceDataAtRow(this.row) as { animals_count?: number } | null;
      const currentAnimalCount = rowData?.animals_count ?? 0;
      callback((value as number) >= currentAnimalCount);
    } catch {
      callback(true);
    }
  };

export const subjectValidatorMessage = (value: ReactNode, rowData?: unknown): string => {
  if (_isNil(value) || value === '') {
    return 'Required field';
  }
  if (typeof value !== 'number') {
    return `${value} must be a number`;
  }
  if (value < 0 || value > 100 || !Number.isInteger(value)) {
    return 'Max. subjects is required and must be an integer value lower than 100';
  }
  const animalCount = (rowData as TreatmentGroup)?.animals_count ?? 0;
  if (value < animalCount) {
    return `There are ${animalCount} animals assigned to this group. Enter a higher number to continue.`;
  }
  return 'Required field';
};

//  Initial Data - Cast to match v12 pattern (partial initial state populated at runtime)
export const initialData = [
  {
    color: ColorEditor.colorForIndex(0),
    name: 'Group 1',
    max_subjects: 0,
  },
] as unknown as Array<TreatmentGroupKeyedMetadata>;

const baseColumnData: ColumnDataResult = {
  colHeaders: [requiredColumnHeader('Color'), requiredColumnHeader('Name'), requiredColumnHeader('No. of animals')],
  columns: [
    {
      data: 'color',
      value: 'Color',
      type: ColorEditor.type,
      width: 110,
    },
    {
      data: 'name',
      value: 'Name',
      type: 'text',
      width: 200,
      validator: createHotRequiredValidator('Name', true, textValidatorFactory(255)),
    },
    {
      data: 'max_subjects',
      value: 'Max Subjects',
      type: 'numeric',
      width: 165,
      validator: subjectValidator(),
      validationErrorMessage: subjectValidatorMessage,
    },
  ],
};

export const settings = {
  ...baseColumnData,
  columnSorting: false,
  className: 'htMiddle',
  minSpareRows: 1,
  manualColumnResize: true,
  rowHeights: 50,
  height: 500,
  rowHeaders: true,
  outsideClickDeselects: false,
};

export const metadataToColumnSettingMapper = (metadata: MetadataField): ColumnSettingMapperResult => {
  const metaID = metadata.id;
  switch (metadata.field_type) {
    case 'select': {
      return {
        columnHeader: metadata.title,
        columnSettings: {
          data: selectColumnDataGetterSetterFactory(metadata),
          metaID,
          width: 200,
          type: MultiSelectEditor.type,
          options: metadata.options,
          isMultiSelect: false,
          validator: MultiSelectEditor.validatorFactory(metadata.options ?? [], false, metadata.required ? 1 : 0, 1),
        },
      };
    }
    case 'lookup_select': {
      return {
        columnHeader: metadata.title,
        columnSettings: {
          data: selectColumnDataGetterSetterFactory(metadata),
          metaID,
          width: 200,
          type: MultiSelectEditor.type,
          optionsLookupId: metadata.id,
          required: metadata.required,
          isMultiSelect: false,
        },
      };
    }
    case 'multi_select': {
      return {
        columnHeader: metadata.title,
        columnSettings: {
          data: multiSelectColumnDataGetterSetterFactory(metadata),
          metaID,
          width: 200,
          type: MultiSelectEditor.type,
          options: metadata.options,
          jsonEncoded: true,
          required: metadata.required,
        },
      };
    }
    case 'lookup_multi_select': {
      return {
        columnHeader: metadata.title,
        columnSettings: {
          data: multiSelectColumnDataGetterSetterFactory(metadata),
          metaID,
          width: 200,
          type: MultiSelectEditor.type,
          optionsLookupId: metadata.id,
          required: metadata.required,
          jsonEncoded: true,
        },
      };
    }
    case 'text': {
      return {
        columnHeader: metadata.title,
        columnSettings: {
          data: columnDataGetterSetterFactory(metadata),
          metaID,
          width: 200,
          type: 'text',
          metadataType: 'text',
          validator: createHotRequiredValidator(metadata.title, metadata.required, textValidatorFactory()),
        },
      };
    }
    case 'date': {
      return {
        columnHeader: metadata.title,
        columnSettings: {
          data: columnDataGetterSetterFactory(metadata),
          metaID,
          width: 150,
          type: DateEditorWrapper.type,
          metadataType: 'date',
          validator: dateValidator(metadata.required),
        },
      };
    }
    case 'numeric': {
      return {
        columnHeader: metadata.title,
        columnSettings: {
          data: columnDataGetterSetterFactory(metadata),
          metaID,
          width: 150,
          type: ExponentialEditor.type,
          validator: ExponentialEditor.numericValidatorFactory(metadata.required),
        },
      };
    }
    default: {
      throw new Error(`Unrecognised metadata field_type [${metadata.field_type}]`);
    }
  }
};

export const generateColumnData = (
  displayedGroupMetadata: Array<Partial<MetadataField> & { required?: boolean }>
): ColumnDataResult =>
  displayedGroupMetadata.reduce<ColumnDataResult>(
    (acc, metadata) => {
      if (metadata.field_type) {
        const { columnHeader, columnSettings } = metadataToColumnSettingMapper(metadata as MetadataField);
        let header = columnHeader;
        if (metadata.active === false) {
          header = inactiveColumn(columnHeader);
        } else if (metadata.required) {
          header = requiredColumnHeader(columnHeader);
        }

        return {
          colHeaders: [...acc.colHeaders, header],
          columns: [...acc.columns, columnSettings],
        };
      }
      return acc;
    },
    { colHeaders: [...baseColumnData.colHeaders], columns: [...baseColumnData.columns] }
  );
